import {SIGNED_APPLICATION_EVENT_TYPE, UNIT_GROUPING} from "../constants/types";
import { get, post, deleteRequest, PageResult, BASE_URL, fetchFile } from "./Common";
import {EntrataDebugResponse} from "./IntegrationEntrataTypes";

export interface LinkedProperties {
  propertyId: number;
  l1Id: number;
  unitGrouping: UNIT_GROUPING;
  enabled: boolean;
  importApplicants: boolean;
  importSignedApplicants: boolean;
  signedApplicationEventType: SIGNED_APPLICATION_EVENT_TYPE | undefined;
  importFullRooms: boolean;
  notifyFullRooms: boolean;
  importMonthToMonth: boolean;
  importAllCurrentLeases: boolean;
  regEx: string | null;
  propertyName: string;
  lastRun: [number, number, number, number, number, number];
}

export interface EntrataUser {
  id: number;
  subDomain: string;
  userName: string;
  properties: LinkedProperties[];
}

export interface IIntegrationData {
 accApi?: boolean;
 kineticApi?: boolean;
 importEnabled: boolean;
 importCompleteUnits: boolean;
 inviteCompleteUnits: boolean;
 lastImportRun: number[];
 mappings: (IIntegrationsMappings | IIntegrationsEntrata | IIntegrationsKinetic)[];
}

export interface IIntegrationsMappings {
  enableAccess: boolean;
  externalNetworkId: number;
  id: number;
  l1Id: number;
  l2Id: number;
  l3Id: number;
  l4Id: number;
  mapping: string;
  networkMappingType: string;
  seasonId: string;
  grouping: string;
}

export interface IIntegrationsKinetic {
  enableAccess: boolean;
  id: number;
  l1Id: number;
  l2Id: number;
  l3Id: number;
  instanceId: string;
  yearOfStudyId: string;
  studentType: string;
  pathwayId: number;
  networkMappingType: string;
  importFieldSelection?: "STUDENT_TYPE" | "PATHWAY_ID" | "BOTH" | "NONE";
}
export interface IIntegrationsEntrataMapping {
  dateType: "START_DATE" | "END_DATE";
  entrataEndDate: [number, number, number];
  entrataStartDate: [number, number, number];
  entrataFloorPlan: string;
  entrataUnitSpace: string;
  entrataPropertyId: string;
  entrataSpaceConfiguration: string | null;
  enableAccess: boolean;
  id: number;
  l1Id: number;
  l2Id: number;
  l3Id: number;
  l4Id: number;
  networkMappingType: string;
}
export interface IIntegrationsEntrata {
  dateType: "START_DATE" | "END_DATE";
  entrataEndDate: [number, number, number];
  entrataStartDate: [number, number, number];
  entrataFloorPlan: string;
  entrataUnitSpace: string;
  entrataPropertyId: string;
  entrataSpaceConfiguration: string | null;
  enableAccess: boolean;
  id: number;
  l1Id: number;
  l2Id: number;
  l3Id: number;
  l4Id: number;
  networkMappingType: string;
  mappings?: IIntegrationsEntrataMapping[];
}

export interface EntrataProperty {
  propertyId: number;
  propertyName: string;
}

export interface EntrataFloorPlans {
  floorPlanId: string;
  name: string;
  unitTypes: EntrataUnitTypes[];
}

export interface EntrataUnitTypes {
  id: number;
  name: string;
}

export interface EntrataNetworkMappings {
  id?: number;
  rowId: number;
  l1Id?: number;
  l2Id?: number;
  l3Id?: number;
  l4Id?: number;
  name?: string;
  updated?: boolean;
  extra?: boolean;
  entrataPropertyId: string;
  valid?: boolean | undefined;
  allowAccess: boolean;
  entrataFloorPlan?: string;
  entrataStartDate?: number[];
  entrataEndDate?: number[];
  entrataSpaceConfiguration?: string;
  entrataUnitSpace?: string;
  dateType?: string;
}

export interface EntrataMappingsResponse {
  entrataFloorPlans: EntrataFloorPlans[];
  entrataNetworkMappings: EntrataNetworkMappings[];
  entrataSpaceConfigurations: string[];
}

export interface ICreateIntegrationsMappings {
  externalId: number | string;
  l1Id: number;
  l2Id: number;
  l3Id?: number;
  l4Id?: number;
  seasonId?: string;
  grouping?: string;
  networkMappingType: string;
  mapping: string;
  enableAccess: boolean;
}

export interface IUpdateIntegrationsMappings {
  id: number;
  mapping?: string;
  seasonId?: string;
  grouping?: string;
  enableAccess?: boolean;
  networkMappingType?: string;
}

export interface ICreateIntegrationsKinetic {
  l1Id: number;
  l2Id: number;
  l3Id?: number;
  instanceId: string;
  yearOfStudyId: string;
  studentType?: string;
  pathwayId?: number;
  networkMappingType: string;
  enableAccess: boolean;
  importFieldSelection: "STUDENT_TYPE" | "PATHWAY_ID" | "BOTH" | "NONE";
}

export interface IUpdateIntegrationsKinetic {
  id: number;
  instanceId?: string;
  yearOfStudyId?: string;
  studentType?: string;
  pathwayId?: number;
  enableAccess?: boolean;
  networkMappingType?: string;
  importFieldSelection?: "STUDENT_TYPE" | "PATHWAY_ID" | "BOTH" | "NONE";
}

export const GetIntegrationsMappings = async (l1Id: number, authToken: string) => {
  const url = `${BASE_URL}/integrations/mappings/${l1Id}`;

  const httpResponse = await get<IIntegrationData>(url, authToken);

  return httpResponse.parsedBody;
};

export const GetIntegrationsEntrataMappings = async (l1Id: number, authToken: string) => {
  const url = `${BASE_URL}/integrations/mappings/${l1Id}`;

  const httpResponse = await get<(IIntegrationsMappings | IIntegrationsEntrata)>(url, authToken);

  return httpResponse.parsedBody;
};

export const createIntegrationsMappings = async (
  l1Id: number,
  data: ICreateIntegrationsMappings,
  authToken: string
) => {
  const url = `${BASE_URL}/integrations/mappings/${l1Id}`;

  const httpResponse = await post<IIntegrationsMappings>(url, authToken, undefined, data);

  return httpResponse;
};

export const deleteIntegrationsMappings = async (l1Id: number, mappingId: number, authToken: string) => {
  const url = `${BASE_URL}/integrations/mappings/${l1Id}/mapping/${mappingId}`;

  const res = await deleteRequest(url, authToken);

  return res;
};

export const updateIntegrationsMappings = async (
  l1Id: number,
  mappingId: number,
  data: IUpdateIntegrationsMappings,
  authToken: string
) => {
  const url = `${BASE_URL}/integrations/mappings/${l1Id}/mapping/${mappingId}`;

  const res = await post(url, authToken, undefined, data);

  return res;
};

export const createIntegrationsKinetic = async (
  l1Id: number,
  data: ICreateIntegrationsKinetic,
  authToken: string
) => {
  const url = `${BASE_URL}/integrations/mappings/${l1Id}`;

  const httpResponse = await post(url, authToken, undefined, data);

  return httpResponse;
};

export const updateIntegrationsKinetic = async (
  l1Id: number,
  mappingId: number,
  data: IUpdateIntegrationsKinetic,
  authToken: string
) => {
  const url = `${BASE_URL}/integrations/mappings/${l1Id}/mapping/${mappingId}`;

  const httpResponse = await post(url, authToken, undefined, data);

  return httpResponse;
};

export const LoadEntrataUsers = async (page: number, authToken: string) => {
  const url = `${BASE_URL}/integrations/entrata/users?page=${page}`;

  const entrataUserHttpResponse = await get<PageResult<EntrataUser>>(url, authToken);

  return entrataUserHttpResponse.parsedBody as PageResult<EntrataUser>;
};

export const LoadAllEntrataUsers = async (authToken: string): Promise<EntrataUser[]> => {
  const first = await LoadEntrataUsers(0, authToken);
  const all: EntrataUser[] = [...(first?.content ?? [])];
  const totalPages = first?.totalPages ?? 1;
  for (let page = 1; page < totalPages; page++) {
    const next = await LoadEntrataUsers(page, authToken);
    if (next?.content?.length) all.push(...next.content);
  }
  return all;
};

export const LoadAvailableProperties = async (entrataUserId: number, authToken: string) => {
  const url = `${BASE_URL}/integrations/entrata/user/${entrataUserId}/properties`;

  const entrataUserHttpResponse = await get<any>(url, authToken);
  if(entrataUserHttpResponse?.parsedBody?.error) {
    return entrataUserHttpResponse.parsedBody;
  }

  return entrataUserHttpResponse.parsedBody as EntrataProperty[];
};

export const LoadAvailableFloorPlans = async (entrataUserId: number, propertyId: number, authToken: string) => {
  const url = `${BASE_URL}/integrations/entrata/user/${entrataUserId}/property/${propertyId}/floorplans`;

  const entrataUserHttpResponse = await get<EntrataMappingsResponse>(url, authToken);

  return entrataUserHttpResponse.parsedBody as EntrataMappingsResponse;
};

export const SaveEntrataMappings = async (
  entrataUserId: number,
  propertyId: number,
  mappings: EntrataNetworkMappings[],
  authToken: string
) => {
  if (mappings.length === 0) {
    return;
  }

  const url = `${BASE_URL}/integrations/entrata/user/${entrataUserId}/property/${propertyId}/mappings`;

  return await post(url, authToken, undefined, mappings);
};

export const RemoveEntrataMapping = async (
  entrataUserId: number,
  propertyId: number,
  mappingId: string,
  authToken: string
) => {
  if (!mappingId) {
    return;
  }

  const url = `${BASE_URL}/integrations/entrata/user/${entrataUserId}/property/${propertyId}/mappings/${mappingId}`;

  const res = await deleteRequest(url, authToken);

  return res;
};

export const EntrataDebug = async (
  entrataUserId: number,
  propertyId: number,
  firstName: string,
  lastName: string,
  customerId: number,
  authToken: string
) => {
  const url = `${BASE_URL}/integrations/entrata/user/${entrataUserId}/property/${propertyId}/debug`;

  const httpResponse = await post<EntrataDebugResponse>(url, authToken, undefined, {
    firstName,
    lastName,
    customerId,
  });
  return httpResponse.parsedBody;
};

export interface LeaseItem {
  zipCode: string
  unitName: string
  unitId: string
  street1: string
  state: string
  startDate: string
  occupancyCount: number
  leaseItemId: string
  leasableItemName: string
  leasableItemMarketingName: string
  leasableItemId: string
  leasableItemFamily: string
  endDate: string
  country: string
  city: string
  buildingName: string
  bedSpace: string
  bedroomName: string
  assetName: string
  assetId: string
}

export interface LeaseEntity {
  originalLeaseId: string
  originalLeaseNumber: string
  moveOutDate: any
  moveInDate?: string
  leaseType: string
  leaseStatus: string
  leaseStartDate: string
  leaseId: string
  leaseNumber: string
  leaseEndDate: string
  leaseCancelledDate?: string
  propertyTermId: string
  propertyTermExternalId: string
  propertyTermName: string
  leaseCategory: any
  items: LeaseItem[]
}

export interface Lease {
  subnetwork: string
  studentId: string
  seasonName: string
  seasonId: string
  seasonExternalId: string
  roommateMatchingPreference: string
  preferredName?: string
  phoneNumber: string
  middleName?: string
  leases: LeaseEntity[]
  lastName: string
  id: string
  gender: string
  floorplan: string
  firstName: string
  externalAccountId: string
  email: string
  applicationCompletedDate: string
  applicationCancelledDate: any
  accountPropertyUniqueId: string
  accountId: string
}


export const AccDebug = async (
    l1Id: number,
    firstName: string | null,
    lastName: string | null,
    leaseId: string | null,
    authToken: string
) => {
  const url = `${BASE_URL}/integrations/acc/l1Id/${l1Id}/debug`;

  const httpResponse = await post(url, authToken, undefined, {
    firstName,
    lastName,
    leaseId
  });
  return httpResponse.parsedBody;
};

export const EntrataRunImport = async (l1Id: number, authToken: string) => {
  const url = `${BASE_URL}/integrations/entrata/import/${l1Id}`;

  const httpResponse = await post(url, authToken, undefined);

  return httpResponse;
};


export const forceImport = async (l1Id: number, importType: string, authToken: string) => {
  const url = `${BASE_URL}/integrations/${importType.toLowerCase()}/import/${l1Id}`;

  const res = await post(url, authToken, undefined);

  return res.parsedBody;
};

export const enableImport = async (l1Id: number, authToken: string, enabled: boolean) => {
  const url = `${BASE_URL}/integrations/acc/${l1Id}`;

  const res = await post(url, authToken, undefined, {enabled});

  return res.parsedBody;
};

// Kinetic Integration API functions
export const forceKineticImport = async (l1Id: number, authToken: string) => {
  const url = `${BASE_URL}/integrations/kinetic/import/${l1Id}`;

  const res = await post(url, authToken, undefined);

  return res.parsedBody;
};

export const enableKineticImport = async (l1Id: number, authToken: string, enabled: boolean) => {
  const url = `${BASE_URL}/integrations/${l1Id}`;

  const res = await post(url, authToken, undefined, {enabled});

  return res.parsedBody;
};

export const deleteIntegrationsKinetic = async (
  l1Id: number,
  id: number,
  authToken: string
) => {
  const url = `${BASE_URL}/integrations/mappings/${l1Id}/mapping/${id}`;

  const httpResponse = await deleteRequest(url, authToken);

  return httpResponse;
};

interface EntrataUserL1CreationRequest {
  l1Id: number;
  propertyName: string;
  importApplicants: boolean;
  importSignedApplicants: boolean;
  signedApplicationEventType?: SIGNED_APPLICATION_EVENT_TYPE;
  importFullRooms: boolean;
  notifyFullRooms: boolean;
  importMonthToMonth: boolean;
  importAllCurrentLeases?: boolean;
  enabled: boolean;
  unitGrouping?: UNIT_GROUPING;
}

export const DisconnectEntrataProperty = async (
  entrataUserId: number,
  l1Id: number,
  propertyId: number,
  authToken: string
) => {
  const url = `${BASE_URL}/integrations/entrata/user/${entrataUserId}/l1/${l1Id}/property/${propertyId}/disconnect`;

  return deleteRequest(url, authToken);
};

export const SaveEntrataUserL1 = async (
  entrataUserId: number,
  propertyId: number,
  request: EntrataUserL1CreationRequest,
  authToken: string
) => {
  const url = `${BASE_URL}/integrations/entrata/user/${entrataUserId}/property/${propertyId}/user`;

  await post(url, authToken, undefined, request);
};

export const setUnitGrouping = async (
  entrataUserId: number,
  propertyId: number,
  request: EntrataUserL1CreationRequest,
  authToken: string
) => {
  const url = `${BASE_URL}/integrations/entrata/user/${entrataUserId}/property/${propertyId}/user`;

  return post(url, authToken, undefined, request);
};

export const migrateEntrata = async (
    l1Id: number,
    oldPropertyId: number,
    newPropertyId: number,
    fromType: string,
    toType: string,
    file: Blob,
    authToken: string
) => {
  const url = `${BASE_URL}/integrations/migrate/${l1Id}?newPropertyId=${newPropertyId}&oldPropertyId=${oldPropertyId}&fromType=${fromType}&toType=${toType}`;

  let formData = new FormData();
  formData.append("file", file);

  return post(url, authToken, undefined, formData);
};

export const migrateSftpToEntrata = async (
    l1Id: number,
    newPropertyId: number,
    fromType: string,
    toType: string,
    file: Blob,
    authToken: string
) => {
  const url = `${BASE_URL}/integrations/migrate/${l1Id}?newPropertyId=${newPropertyId}&fromType=${fromType}&toType=${toType}`;

  let formData = new FormData();
  formData.append("file", file);

  return post(url, authToken, undefined, formData);
};

export type MigrationFromType = "ENTRATA" | "TenantKey" | "ACCSFApi";

export interface AccToEntrataMigrationParams {
  l1Id: number;
  fromType: MigrationFromType;
  portfolioId: string;
  entrataUserId: number;
  entrataPropertyId: number;
}

export interface MigrationPreviewResult {
  toBeMigrated?: number;
  toBeRemoved?: number;
  missingFromCsv?: number;
  missingMembershipIds?: string[];
}

export const getMissingMembershipsCount = (preview: MigrationPreviewResult) => {
  const missingFromCsv = preview.missingFromCsv ?? 0;
  const missingIdsLen = preview.missingMembershipIds?.length ?? 0;
  return Math.max(missingFromCsv, missingIdsLen);
};

export const isMigrationPreviewValid = (preview: MigrationPreviewResult) =>
  getMissingMembershipsCount(preview) === 0;

const buildMigrationQuery = (
  params: AccToEntrataMigrationParams,
  dryRun: boolean
) => {
  const query = new URLSearchParams();
  query.set("fromType", params.fromType);
  query.set("toType", "ENTRATA");
  query.set("portfolioId", params.portfolioId);
  query.set("entrataUserId", String(params.entrataUserId));
  query.set("entrataPropertyId", String(params.entrataPropertyId));
  if (dryRun) {
    query.set("dryRun", "true");
  }
  return query.toString();
};

export const previewAccToEntrataMigration = async (
  params: AccToEntrataMigrationParams,
  file: Blob,
  authToken: string
) => {
  const url = `${BASE_URL}/integrations/migrate/${params.l1Id}?${buildMigrationQuery(params, true)}`;
  const formData = new FormData();
  formData.append("file", file);
  const response = await post<MigrationPreviewResult>(url, authToken, undefined, formData);
  return response;
};

export const executeAccToEntrataMigration = async (
  params: AccToEntrataMigrationParams,
  file: Blob,
  authToken: string
) => {
  const url = `${BASE_URL}/integrations/migrate/${params.l1Id}?${buildMigrationQuery(params, false)}`;
  const formData = new FormData();
  formData.append("file", file);
  return post(url, authToken, undefined, formData);
};

export const downloadMigrationCsvTemplate = async (l1Id: number, authToken: string) => {
  const url = `${BASE_URL}/integrations/migrate/${l1Id}/template`;
  return fetchFile(url, authToken);
};

// ACCSFApi → TenantKey migration. The CSV is optional: when present, its lease
// numbers override existing ones before the migration runs; when omitted, the
// migration proceeds with current data and the request carries no body.
export const migrateAccToTenantKey = async (
  l1Id: number,
  portfolioId: string,
  file: File | undefined,
  authToken: string
) => {
  const query = new URLSearchParams();
  query.set("fromType", "ACCSFApi");
  query.set("toType", "TenantKey");
  query.set("portfolioId", portfolioId);
  const url = `${BASE_URL}/integrations/migrate/${l1Id}?${query.toString()}`;

  if (file) {
    const formData = new FormData();
    formData.append("file", file);
    return post(url, authToken, undefined, formData);
  }

  return post(url, authToken, undefined);
};

// The ACC→TenantKey endpoint answers 400 import.csv.invalid_format when the
// uploaded CSV is missing a required header or value. Detecting it lets the
// caller show the error inline on the upload step instead of as a toast.
export const isCsvFormatError = (
  res: { status?: number; parsedBody?: unknown } | null | undefined
): boolean => {
  if (!res || res.status !== 400) return false;
  const body = res.parsedBody as { message?: string; error?: string } | undefined;
  const code = `${body?.message ?? ""} ${body?.error ?? ""}`;
  return /invalid_format|import\.csv/i.test(code);
};

export interface ExportToEntrataRequest {
  networkId: number;
  matchingRoundId: number;
  users: string[]; // membershipIds
}

export const exportGroupsToEntrata = async (
  request: ExportToEntrataRequest,
  authToken: string
) => {
  const url = `${BASE_URL}/integrations/entrata/export`;

  return post(url, authToken, undefined, request);
};

export interface SendBackRoommatesRequest {
  matchingRoundId: number;
  users?: string[]; // membershipIds - optional for sending all users
}

export const sendBackRoommates = async (
  l1Id: number,
  l2Id: number,
  request: SendBackRoommatesRequest,
  authToken: string
) => {
  const url = `${BASE_URL}/integrations/entrata/l1Id/${l1Id}/l2Id/${l2Id}/sendBackRoommates`;

  return post(url, authToken, undefined, request);
};

export interface MatchingRoundForEntrata {
  l2Id: number;
  l2Name: string;
  matchingRoundStatus: string | null;
  matchingStartDate: string;
  matchingEndDate: string;
  activationStartDate: string;
  activationEndDate: string;
}

export const getMatchingRoundsForEntrata = async (
  l1Id: number,
  authToken: string
) => {
  const url = `${BASE_URL}/integrations/entrata/l1Id/${l1Id}/matchingRounds`;

  const httpResponse = await get<MatchingRoundForEntrata[]>(url, authToken);

  return httpResponse.parsedBody as MatchingRoundForEntrata[];
};

export interface ExportRun {
  id: string;
  startTime: number[]; // Array format: [year, month, day, hour, minute, second]
  endTime: number[]; // Array format: [year, month, day, hour, minute, second]
  numberOfSuccesses: number;
  numberOfFailures: number;
  singleRoommates: number;
  status: string;
}

export interface PreviousExportsResponse {
  runs: ExportRun[];
}

export const getPreviousExports = async (
  l1Id: number,
  l2Id: number,
  authToken: string
) => {
  const url = `${BASE_URL}/integrations/entrata/l1Id/${l1Id}/l2Id/${l2Id}/sendBackRoommates`;

  const httpResponse = await get<PreviousExportsResponse>(url, authToken);

  return httpResponse.parsedBody as PreviousExportsResponse;
};

export const downloadExportCsv = async (
  l1Id: number,
  l2Id: number,
  runId: string,
  authToken: string
) => {
  const url = `${BASE_URL}/integrations/entrata/l1Id/${l1Id}/l2Id/${l2Id}/sendBackRoommates/${runId}.csv`;

  return fetchFile(url, authToken);
};